import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon, Filter, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { MultiSelect } from "../../components/ui/multi-select";
import { Label } from "../../components/ui/label";

/* -------------------- OPTIONS -------------------- */
const dimensionOptions = [
  { label: "Date", value: "date" },
  { label: "Domain", value: "domain" },
  { label: "Ad Format", value: "ad_format" },
  { label: "Ad Size", value: "ad_size" },
  { label: "Advertizer", value: "advertizer" },
  { label: "Platform", value: "platform" },
  { label: "Partner", value: "partner" },
  { label: "Category", value: "category" },
  { label: "Article Id", value: "articleId" },
  { label: "Browser", value: "browser" },
  { label: "Server / Client", value: "server_or_client" },
];

const metricOptions = [
  { label: "Revenue", value: "revenue" },
  { label: "Monetized Impressions", value: "monetized_impressions" },
  { label: "Monetized eCPM", value: "monetized_ecpm" },
  { label: "Average Latency", value: "avg_latency" },
  { label: "Request Count", value: "request_count" },
  { label: "Response Count", value: "response_count" },
  { label: "Bid Rate", value: "bid_rate" },
  { label: "Win Rate", value: "win_rate" },
];

/* -------------------- HELPERS -------------------- */
const parseFiltersFromSearch = (search) => {
  const params = new URLSearchParams(search);

  return {
    dateFrom: params.get("startDate")
      ? new Date(params.get("startDate"))
      : undefined,

    dateTo: params.get("endDate")
      ? new Date(params.get("endDate"))
      : undefined,

    dimensions: params.get("dimensions")
      ? params.get("dimensions").split(",")
      : [],

    metrics: params.get("metrics")
      ? params.get("metrics").split(",")
      : [],
  };
};

/* -------------------- COMPONENT -------------------- */
const FilterModal = ({ filters, onFiltersChange, metricOptions: propMetricOptions }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const today = new Date();

  const [open, setOpen] = useState(!location.search); // open only if no filters

  const [localFilters, setLocalFilters] = useState(() =>
    parseFiltersFromSearch(location.search)
  );

  /* Sync URL -> UI (back/forward, manual URL edit) */
  useEffect(() => {
    setLocalFilters(parseFiltersFromSearch(location.search));
  }, [location.search]);

  /* -------------------- HANDLERS -------------------- */
  const handleApply = () => {
    const query = new URLSearchParams();

    if (localFilters.dateFrom) {
      // use local date to avoid timezone issues
      query.set("startDate", format(localFilters.dateFrom, "yyyy-MM-dd"));
    }

    if (localFilters.dateTo) {
      query.set("endDate", format(localFilters.dateTo, "yyyy-MM-dd"));
    }

    if (localFilters.dimensions.length > 0) {
      query.set("dimensions", localFilters.dimensions.join(","));
    }

    if (localFilters.metrics.length > 0) {
      query.set("metrics", localFilters.metrics.join(","));
    }

    navigate({
      pathname: "/services/prebidAds/dashboard",
      search: `?${query.toString()}`,
    });

    onFiltersChange(localFilters);
    setOpen(false);
  };

  const handleReset = () => {
    const resetFilters = {
      dateFrom: undefined,
      dateTo: undefined,
      dimensions: [],
      metrics: [],
    };

    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
    navigate("/services/prebidAds/dashboard");
    
  };

  const activeFiltersCount =
    (localFilters.dateFrom ? 1 : 0) +
    (localFilters.dateTo ? 1 : 0) +
    localFilters.dimensions.length +
    localFilters.metrics.length;

  /* -------------------- RENDER -------------------- */
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="gap-2 bg-secondary/50 border-border hover:bg-secondary/80"
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeFiltersCount > 0 && (
            <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-primary text-primary-foreground">
              {activeFiltersCount}
            </span>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[500px] bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-primary" />
            Filter Metrics
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* DATE RANGE */}
          <div className="space-y-3">
            <Label>Date Range</Label>

            <div className="grid grid-cols-2 gap-3">
              {/* FROM */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal bg-secondary/50",
                      !localFilters.dateFrom && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {localFilters.dateFrom
                      ? format(localFilters.dateFrom, "MMM d, yyyy")
                      : "Start date"}
                  </Button>
                </PopoverTrigger>

                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={localFilters.dateFrom}
                    disabled={(date) => date > today}
                    onSelect={(date) => {
                      if (!date) return;
                      if (localFilters.dateTo && date > localFilters.dateTo)
                        return;
                      setLocalFilters({ ...localFilters, dateFrom: date });
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* TO */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal bg-secondary/50",
                      !localFilters.dateTo && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {localFilters.dateTo
                      ? format(localFilters.dateTo, "MMM d, yyyy")
                      : "End date"}
                  </Button>
                </PopoverTrigger>

                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={localFilters.dateTo}
                    disabled={(date) => date > today}
                    onSelect={(date) => {
                      if (!date) return;
                      if (localFilters.dateFrom && date < localFilters.dateFrom)
                        return;
                      setLocalFilters({ ...localFilters, dateTo: date });
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* DIMENSIONS */}
          <div className="space-y-3">
            <Label>Dimensions</Label>
            <MultiSelect
              options={dimensionOptions}
              selected={localFilters.dimensions}
              onChange={(dimensions) =>
                setLocalFilters({ ...localFilters, dimensions })
              }
              placeholder="Select dimensions..."
            />
          </div>

          {/* METRICS */}
          <div className="space-y-3">
            <Label>Metrics</Label>
            <MultiSelect
              options={propMetricOptions || metricOptions}
              selected={localFilters.metrics}
              onChange={(metrics) =>
                setLocalFilters({ ...localFilters, metrics })
              }
              placeholder="Select metrics..."
            />
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="ghost"
            onClick={handleReset}
            className="text-muted-foreground"
          >
            <X className="h-4 w-4 mr-2" />
            Reset
          </Button>

          <Button onClick={handleApply} disabled={activeFiltersCount === 0}>
            Apply Filters
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FilterModal;
